import React, { useEffect, useMemo, useState, useCallback, lazy, Suspense } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  IconButton,
  Drawer,
  Divider,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Tooltip,
  Button,
  Stack,
  Collapse,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Badge,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import PersonIcon from '@mui/icons-material/Person'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import BadgeIcon from '@mui/icons-material/Badge'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import BusinessIcon from '@mui/icons-material/Business'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import FilterListIcon from '@mui/icons-material/FilterList'
import HistoryIcon from '@mui/icons-material/History'
import EventIcon from '@mui/icons-material/Event'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonPinIcon from '@mui/icons-material/PersonPin'
import ViewListIcon from '@mui/icons-material/ViewList'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import EditIcon from '@mui/icons-material/Edit'
import CancelIcon from '@mui/icons-material/Cancel'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import MergeIcon from '@mui/icons-material/Merge'
import NotesIcon from '@mui/icons-material/Notes'
import HomeIcon from '@mui/icons-material/Home'
import ContactsIcon from '@mui/icons-material/Contacts'
import SettingsIcon from '@mui/icons-material/Settings'
import InfoIcon from '@mui/icons-material/Info'
import TuneIcon from '@mui/icons-material/Tune'
import ClearAllIcon from '@mui/icons-material/ClearAll'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import PsychologyIcon from '@mui/icons-material/Psychology'
import AssignmentIcon from '@mui/icons-material/Assignment'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import SupportPatientPackRequestsPanel from 'src/supportPortal/components/paymentLinks/SupportPatientPackRequestsPanel'
import PatientMonitoringDialog from 'src/supportPortal/components/PatientMonitoringDialog'
import EngagementSettingsDialog from 'src/supportPortal/components/EngagementSettingsDialog'
import RebookingDialog from 'src/supportPortal/components/RebookingDialog'
import EngagementTabContent from 'src/supportPortal/components/EngagementTabContent'
import { getSupportPatientPackHistory } from 'src/supportPortal/services/paymentLinksService'
import JoiningDetailsContent from 'src/clinic/components/ClinicManageBookingsPage/slotItem/components/JoiningDetailsContent'
import { PatientForm } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { canManageMonitoring } from 'src/supportPortal/services/patientMonitoringService'
import { canManageEngagement } from 'src/supportPortal/services/patientEngagementService'
import { usePatientReferenceData } from './hooks/usePatientReferenceData'
import { usePatientTableData } from './hooks/usePatientTableData'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { formatPhoneNumber } from 'src/consumer/helpers/utilFunctions'
import { displayNamesOfEventTypes, GENDER_LIST_ONBOARDING, allAvailableEventTypes, BOOKING_SESSION_STATUS, BOOKING_SESSION_STATUS_MAP, BOOKING_PAYMENT_STATUSES, SESSION_PLATFORM_ONLINE, SESSION_PLATFORM_OFFLINE, OFFLINE_PAYMENT_METHODS_LIST } from 'src/consumer/constants'
import { getBookedSlotStripColor } from 'src/clinic/components/ClinicManageBookingsPage/utils/bookedSlotColorUtils'
import { getOrdinalSuffixForSuperScript } from 'src/consumer/helpers/utilFunctions'
import VideoCallIcon from '@mui/icons-material/VideoCall'
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle'
import PaymentIcon from '@mui/icons-material/Payment'
import ReceiptIcon from '@mui/icons-material/Receipt'
import LinkIcon from '@mui/icons-material/Link'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { getCPDataWithoutParams, getCPDataWithParams } from 'src/clinic/services'
import { getSPDataWithoutParams, postSPDataWithoutParams } from 'src/supportPortal/services/services'
import { ReceiptPrintDialogPaper } from 'src/clinic/components/ClinicManageBookingsPage/receiptPrinting/styledComponents'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const ReceiptPrintingComponent = lazy(() =>
  import('src/clinic/components/ClinicManageBookingsPage/receiptPrinting/ReceiptPrintingComponent')
)

// Filter options from API docs
const EVENT_TYPE_OPTIONS = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'therapy-session', label: 'Therapy Session' },
  { value: 'screening', label: 'Screening' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'nutrition-consultation', label: 'Nutrition Consultation' },
  { value: '1on1', label: '1-on-1' },
  { value: 'interview', label: 'Interview' },
  { value: 'webinar', label: 'Webinar' },
]

function normalizeSupportContactEventType(eventType) {
  const normalized = `${eventType || ''}`.trim().toLowerCase()
  if (!normalized) {
    return ''
  }
  if (normalized.includes('therapy')) {
    return 'therapy-session'
  }
  if (normalized.includes('consult')) {
    return 'consultation'
  }
  if (normalized.includes('screen') || normalized.includes('assessment')) {
    return 'screening'
  }
  return normalized
}

function getPatientSupportContactEventTypes(patient) {
  const rawEventTypes = [
    ...(Array.isArray(patient?.eventTypes) ? patient.eventTypes : []),
    patient?.eventType,
  ]

  return [...new Set(rawEventTypes.map(normalizeSupportContactEventType).filter(Boolean))]
}

const MONITORING_SCOPE_OPTIONS = [
  { value: '', label: 'Any Scope' },
  { value: 'PATIENT_ONLY', label: 'Patient Only' },
  { value: 'PATIENT_AND_MENTOR', label: 'Patient & Mentor' },
]

const MONITORING_POLICY_OPTIONS = [
  { value: '', label: 'Any Policy' },
  { value: 'ALL', label: 'All' },
  { value: 'FIRST_PER_ACTION', label: 'First Per Action' },
]

const MONITORING_EVENT_MODES = [
  { value: 'aumhum', label: 'Aumhum' },
  { value: 'offline', label: 'Offline' },
  { value: 'home-visit', label: 'Home Visit' },
]

const MONITORING_ACTIONS = [
  { value: 'JOIN', label: 'Join Session' },
  { value: 'LEAVE', label: 'Leave Session' },
  { value: 'JOIN_AGORA_CALL', label: 'Join Video Call' },
  { value: 'LEAVE_AGORA_CALL', label: 'Leave Video Call' },
  { value: 'AUTO_CONNECT_PAUSED', label: 'Auto-Connect Paused' },
  { value: 'AUTO_CONNECT_RESUMED', label: 'Auto-Connect Resumed' },
  { value: 'CAMERA_ON', label: 'Camera On' },
  { value: 'CAMERA_OFF', label: 'Camera Off' },
  { value: 'MIC_ON', label: 'Mic On' },
  { value: 'MIC_OFF', label: 'Mic Off' },
  { value: 'DISCONNECTED', label: 'Disconnected' },
  { value: 'MARKED_STALE', label: 'Marked Stale' },
  { value: 'THROWN_OUT', label: 'Thrown Out' },
  { value: 'FOUND_ABSENT', label: 'Found Absent' },
  { value: 'CHECKIN', label: 'Check-in' },
]

const MONITORED_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'true', label: 'Monitored' },
  { value: 'false', label: 'Not Monitored' },
]

const DRAWER_TABS = {
  DETAILS: 'details',
  HISTORY: 'history',
  PACKS: 'packs',
  ENGAGEMENT: 'engagement',
}

// Styled components
const PatientCard = styled(Paper)(({ theme, selected }) => ({
  width: '100%',
  boxSizing: 'border-box',
  paddingTop: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: 0,
  borderRadius: theme.spacing(1.5),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  border: selected ? '2px solid #6779e3' : '1.5px solid rgba(226, 232, 240, 0.8)',
  backgroundColor: selected ? 'rgba(103, 121, 227, 0.05)' : '#fff',
  boxShadow: selected ? '0 4px 12px rgba(103, 121, 227, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  '&:hover': {
    borderColor: '#6779e3',
    backgroundColor: 'rgba(103, 121, 227, 0.03)',
    boxShadow: '0 4px 12px rgba(103, 121, 227, 0.15)',
    transform: 'translateY(-2px)',
  },
}))

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: 700,
    maxWidth: '95vw',
    height: '100vh',
    maxHeight: '100vh',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
    [theme.breakpoints.down('md')]: {
      width: '100vw',
    },
  },
}))

const FilterSection = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  border: '1px solid rgba(226, 232, 240, 0.8)',
  overflow: 'hidden',
}))

const FilterHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2),
  backgroundColor: '#fafbfc',
  borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
}))

const FilterBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}))

const CompactSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '13px',
  },
  '& .MuiOutlinedInput-root': {
    fontSize: '13px',
    backgroundColor: '#fff',
  },
  '& .MuiSelect-select': {
    padding: '10px 14px',
  },
}))

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  backgroundColor: selected ? 'rgba(103, 121, 227, 0.08)' : 'transparent',
  '&:hover': {
    backgroundColor: selected ? 'rgba(103, 121, 227, 0.12)' : 'rgba(103, 121, 227, 0.04)',
  },
}))

const DetailLabel = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(0.5),
}))

const DetailValue = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#1e293b',
  wordBreak: 'break-word',
}))

const CopyableField = ({ label, value, monospace = false }) => {
  if (!value) return null

  const handleCopy = () => {
    navigator.clipboard.writeText(String(value))
    toast.success(`${label} copied!`, { autoClose: 1500 })
  }

  return (
    <Box sx={{ mb: 1.5 }}>
      <DetailLabel>{label}</DetailLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DetailValue sx={{ fontFamily: monospace ? 'monospace' : 'inherit' }}>{String(value)}</DetailValue>
        <Tooltip title="Copy">
          <IconButton size="small" onClick={handleCopy} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

// Multi-select component
const MultiSelectFilter = ({ label, value = [], options, onChange, disabled = false }) => (
  <CompactSelect size="small" fullWidth disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      value={value}
      onChange={(e) => onChange(e.target.value)}
      input={<OutlinedInput label={label} />}
      renderValue={(selected) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.length <= 2
            ? selected.map((v) => (
                <Chip key={v} label={options.find((o) => o.value === v)?.label || v} size="small" sx={{ height: 20, fontSize: '11px' }} />
              ))
            : `${selected.length} selected`}
        </Box>
      )}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value} dense>
          <Checkbox checked={value.includes(option.value)} size="small" />
          <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px' }} />
        </MenuItem>
      ))}
    </Select>
  </CompactSelect>
)

export default function PatientList() {
  const {
    mentors,
    clinicBrands,
    clinicLocations,
    referenceData,
    loading: referenceLoading,
    fetchReferenceData,
  } = usePatientReferenceData()

  const {
    patients,
    loading: patientsLoading,
    pagination,
    filters,
    activeFilterCount,
    fetchData,
    handleSearch,
    handleFilterChange,
    handleMultipleFilterChange,
    handlePageChange,
    clearFilters,
  } = usePatientTableData(referenceData)

  // Local state
  const [viewMode, setViewMode] = useState('tile')
  const [searchValue, setSearchValue] = useState('')
  const [selectedPatient, setSelectedPatient] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerTab, setDrawerTab] = useState(DRAWER_TABS.DETAILS)
  const [patientEditDialogOpen, setPatientEditDialogOpen] = useState(false)
  const [importedFromItems, setImportedFromItems] = useState([])
  const [visitHistory, setVisitHistory] = useState([])
  const [visitHistoryLoading, setVisitHistoryLoading] = useState(false)
  const [visitHistoryEventTypes, setVisitHistoryEventTypes] = useState([]) // Filter for visit history
  const [packHistory, setPackHistory] = useState([])
  const [packHistoryLoading, setPackHistoryLoading] = useState(false)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [monitoringDialogOpen, setMonitoringDialogOpen] = useState(false)
  const [monitoringPatient, setMonitoringPatient] = useState(null)
  const [engagementDialogOpen, setEngagementDialogOpen] = useState(false)
  const [engagementPatient, setEngagementPatient] = useState(null)
  const [rebookDialogOpen, setRebookDialogOpen] = useState(false)
  const [rebookVisitData, setRebookVisitData] = useState(null)
  const [joiningLinksDialogOpen, setJoiningLinksDialogOpen] = useState(false)
  const [joiningLinksEventData, setJoiningLinksEventData] = useState(null)
  const [joiningLinksLoading, setJoiningLinksLoading] = useState(false)
  const [openReceiptPrintingDialog, setOpenReceiptPrintingDialog] = useState(false)
  const [dataForReceiptPrinting, setDataForReceiptPrinting] = useState({ eventData: null, eventId: null })

  const openReceiptDialog = useCallback((visit) => {
    setDataForReceiptPrinting({ eventData: visit, eventId: visit?.id })
    setOpenReceiptPrintingDialog(true)
  }, [])

  const loadImportedFromItems = useCallback(async () => {
    try {
      const response = await getCPDataWithParams('/api/secure/cc/support/reference/imported-from-ids')
      if (response?.status === 200 && Array.isArray(response?.data)) {
        setImportedFromItems(response.data)
      }
    } catch (error) {
      console.error('Error loading imported-from reference data:', error)
    }
  }, [])

  const receiptCurrentClinic = useMemo(() => {
    const eventData = dataForReceiptPrinting.eventData
    if (!eventData) return null
    const fromRef = eventData?.clinicId && Array.isArray(clinicLocations)
      ? clinicLocations.find((loc) => String(loc?.id) === String(eventData.clinicId))
      : null
    const fromBrandRef =
      eventData?.clinicBrandId && Array.isArray(clinicBrands)
        ? clinicBrands.find((b) => String(b?.id) === String(eventData.clinicBrandId))
        : null
    const title =
      eventData?.clinicLocation?.title ||
      eventData?.clinicLocation?.name ||
      eventData?.clinicName ||
      eventData?.clinicLocationName ||
      fromRef?.title ||
      fromRef?.clinicName ||
      fromRef?.name
    const address =
      eventData?.clinicLocation?.address ||
      eventData?.address ||
      fromRef?.address
    const mobile =
      eventData?.clinicLocation?.mobile ||
      eventData?.mobile ||
      fromRef?.mobile
    const clinicBrandName =
      eventData?.clinicBrandName ||
      eventData?.clinicBrandData?.brandName ||
      eventData?.clinicBrandData?.name ||
      eventData?.clinicBrandData?.title ||
      fromBrandRef?.brandName ||
      fromBrandRef?.title
    const clinicBrandShortName =
      eventData?.clinicBrandData?.shortName || fromBrandRef?.shortName
    const brandName =
      eventData?.clinicBrandData?.brandName ||
      eventData?.clinicBrandData?.name ||
      eventData?.clinicBrandData?.title ||
      fromBrandRef?.brandName ||
      fromBrandRef?.title
    return {
      clinicBrandName,
      clinicBrandShortName,
      brandName,
      title,
      clinicLocationName: title,
      location: address != null ? { address } : null,
      address,
      mobile,
    }
  }, [dataForReceiptPrinting.eventData, clinicLocations, clinicBrands])

  // Fetch public joining links for a booked event
  // Uses the same API as the day calendar: /api/secure/cc/support/events/video-session-tokens/get
  const fetchPublicJoiningLinks = async (bookedEventId) => {
    if (!bookedEventId) return null
    try {
      const resp = await getCPDataWithParams('/api/secure/cc/support/events/video-session-tokens/get', {
        'booked-event-ids': bookedEventId,
      })
      if (resp?.status === 200 && Array.isArray(resp?.data)) {
        return resp.data
      }
    } catch (error) {
      console.error('Error fetching public joining links:', error)
    }
    return null
  }

  // Handle opening joining links dialog
  const handleOpenJoiningLinksDialog = async (visit) => {
    setJoiningLinksDialogOpen(true)
    setJoiningLinksLoading(true)
    setJoiningLinksEventData(visit)

    // Fetch public joining links
    const publicLinks = await fetchPublicJoiningLinks(visit?.id)
    
    // Update event data with public joining links
    setJoiningLinksEventData({
      ...visit,
      publicJoiningLinks: publicLinks || [],
    })
    setJoiningLinksLoading(false)
  }

  // Check if user can manage monitoring
  const hasMonitoringPermission = useMemo(() => canManageMonitoring(), [])
  const hasEngagementPermission = useMemo(() => canManageEngagement(), [])
  
  // Check if user can rebook for a specific event type
  // L3 Therapy Support: therapy-session, screening (assessment)
  // Clinic Success Manager/Admin: consultation
  const canRebookEventType = useCallback((eventType) => {
    const roles = getAllRolesInBoolean()
    
    // L3 Therapy Support can rebook therapy sessions and screenings/assessments
    if (roles.isL3TherapySupport) {
      if (eventType === allAvailableEventTypes.therapySession || 
          eventType === allAvailableEventTypes.screening ||
          eventType === 'assessment') {
        return true
      }
    }
    
    // Clinic Success Manager/Admin can rebook consultations
    if (roles.isClinicSuccessManager || roles.isClinicSuccessAdmin) {
      if (eventType === allAvailableEventTypes.consultation) {
        return true
      }
    }
    
    // Admins can rebook anything
    if (roles.isAdmin) {
      return true
    }
    
    return false
  }, [])

  // Filter clinic locations based on selected brand
  const filteredClinicLocations = useMemo(() => {
    if (!filters.clinicBrandId) return clinicLocations
    return clinicLocations.filter((loc) => loc.clinicBrandId === filters.clinicBrandId)
  }, [clinicLocations, filters.clinicBrandId])

  useEffect(() => {
    fetchReferenceData()
  }, [fetchReferenceData])

  useEffect(() => {
    if (!referenceLoading && Object.keys(referenceData).length > 0) {
      // Auto-select clinic brand if only one exists
      if (clinicBrands.length === 1 && !filters.clinicBrandId) {
        handleFilterChange('clinicBrandId', clinicBrands[0]?.id)
      } else {
        // Fetch data regardless of clinic brand selection
        fetchData()
      }
    }
  }, [referenceLoading, referenceData])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== filters.search) {
        handleSearch('search', searchValue, 2)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  // Load visit history with optional eventType filter
  const loadVisitHistory = useCallback(async (patientId, clinicId, eventTypes = []) => {
    if (!patientId) return
    setVisitHistoryLoading(true)
    try {
      // Build query params
      let url = `/api/secure/cc/support/booked-event-history-of-a-patient?patientId=${patientId}`
      if (clinicId) url += `&clinicId=${clinicId}`
      url += '&sortDescendingOnStartTime=true&limit=100'
      
      // Add eventType filters (repeat param for each type)
      if (eventTypes && eventTypes.length > 0) {
        eventTypes.forEach((et) => {
          url += `&eventType=${et}`
        })
      }
      
      const response = await getCPDataWithoutParams(url)
      if (
        response?.status === 200 &&
        isCommonResponseSuccessful(response?.data?.code) &&
        Array.isArray(response?.data?.data?.['booked-event-info'])
      ) {
        const bookedEvents = response?.data?.data?.['booked-event-info']
          ?.filter((item) => item?.status === 'booked')
          ?.sort((a, b) => dayjs.utc(b?.startTime).valueOf() - dayjs.utc(a?.startTime).valueOf())
        setVisitHistory(bookedEvents || [])
      } else {
        setVisitHistory([])
      }
    } catch (error) {
      console.error('Error fetching visit history:', error)
      setVisitHistory([])
    } finally {
      setVisitHistoryLoading(false)
    }
  }, [])

  const loadPackHistory = useCallback(async (patientId) => {
    if (!patientId) {
      setPackHistory([])
      return
    }
    setPackHistoryLoading(true)
    try {
      const result = await getSupportPatientPackHistory(patientId)
      if (result?.ok) {
        setPackHistory(result.items || [])
      } else {
        setPackHistory([])
      }
    } catch (error) {
      console.error('Error fetching pack history:', error)
      setPackHistory([])
    } finally {
      setPackHistoryLoading(false)
    }
  }, [])

  // Handle patient click
  const handlePatientClick = (patient) => {
    setSelectedPatient(patient)
    setDrawerOpen(true)
    setDrawerTab(DRAWER_TABS.DETAILS)
    setPatientEditDialogOpen(false)
    setVisitHistoryEventTypes([]) // Reset filter
    loadVisitHistory(patient?.id, patient?.clinicId, [])
    loadPackHistory(patient?.id)
  }

  const refreshSelectedPatientDetails = useCallback(async (patientId, clinicId) => {
    if (!patientId) return
    try {
      let apiUrl = `/api/secure/cc/support/patient/search?patientId=${patientId}`
      if (clinicId) {
        apiUrl += `&clinicId=${clinicId}`
      }
      const response = await getSPDataWithoutParams(apiUrl)
      if (response?.status === 200 && Array.isArray(response?.data) && response?.data?.[0]) {
        setSelectedPatient(response.data[0])
      }
    } catch (error) {
      console.error('Error refreshing patient details:', error)
    }
  }, [])

  const handleOpenPatientEdit = useCallback(() => {
    if (!importedFromItems?.length) {
      loadImportedFromItems()
    }
    setPatientEditDialogOpen(true)
  }, [importedFromItems?.length, loadImportedFromItems])

  const handlePatientEditSaveSuccess = useCallback(async () => {
    const patientId = selectedPatient?.id
    const clinicId = selectedPatient?.clinicId
    setPatientEditDialogOpen(false)
    await fetchData()
    await refreshSelectedPatientDetails(patientId, clinicId)
  }, [fetchData, refreshSelectedPatientDetails, selectedPatient?.clinicId, selectedPatient?.id])

  // Handle visit history filter change
  const handleVisitHistoryFilterChange = (eventTypes) => {
    setVisitHistoryEventTypes(eventTypes)
    if (selectedPatient) {
      loadVisitHistory(selectedPatient.id, selectedPatient.clinicId, eventTypes)
    }
  }

  // Handle monitoring button click
  const handleMonitoringClick = (e, patient) => {
    if (e) e.stopPropagation()
    setMonitoringPatient(patient)
    setMonitoringDialogOpen(true)
  }

  // Handle monitoring update success
  const handleMonitoringSuccess = (updatedPatientInfo) => {
    if (updatedPatientInfo) {
      // Refresh the patient list to show updated monitoring status
      fetchData()
      // Also update the selected patient if drawer is open
      if (selectedPatient?.id === updatedPatientInfo.id) {
        setSelectedPatient(updatedPatientInfo)
      }
    }
  }

  // Handle engagement button click
  const handleEngagementClick = (e, patient) => {
    if (e) e.stopPropagation()
    setEngagementPatient(patient)
    setEngagementDialogOpen(true)
  }

  // Handle engagement update success
  const handleEngagementSuccess = () => {
    // Refresh the patient list if needed
    fetchData()
  }

  // Handle rebook button click
  const handleRebookClick = (e, visit) => {
    if (e) e.stopPropagation()
    setRebookVisitData(visit)
    setRebookDialogOpen(true)
  }

  // Utility functions
  const getPatientAge = (dateOfBirth) => {
    if (!dateOfBirth) return null
    return getAgeFromDobWithTime(dateOfBirth)
  }

  const getContactInfo = (patient) => {
    if (patient?.email) return { type: 'email', value: patient.email }
    if (patient?.mobile) {
      const phoneDetails = formatPhoneNumber(patient.mobile)
      const formatted = phoneDetails?.formattedPhoneNumber
        ? `(${phoneDetails.countryCode}) ${phoneDetails.formattedPhoneNumber}`
        : patient.mobile
      return { type: 'mobile', value: formatted }
    }
    return { type: 'none', value: null }
  }

  const getAvatarColor = (gender) => {
    if (!gender) return 'rgba(103, 121, 227, 0.1)'
    const g = gender.toLowerCase()
    if (g === 'male') return 'rgba(59, 130, 246, 0.1)'
    if (g === 'female') return 'rgba(236, 72, 153, 0.1)'
    return 'rgba(103, 121, 227, 0.1)'
  }

  const getIconColor = (gender) => {
    if (!gender) return '#6779e3'
    const g = gender.toLowerCase()
    if (g === 'male') return '#3b82f6'
    if (g === 'female') return '#ec4899'
    return '#6779e3'
  }

  const getGenderLabel = (gender) => {
    if (!gender) return null
    return GENDER_LIST_ONBOARDING?.find((i) => i.value === gender)?.label || gender
  }

  const formatDate = (d) => (d ? dayjs(d).format('DD MMM YYYY, hh:mm A') : '-')
  const formatDateShort = (d) => (d ? dayjs(d).format('DD MMM YYYY') : '-')

  const getEventTypeColor = (t) =>
    ({ consultation: '#6779e3', 'therapy-session': '#7450aa', therapySession: '#7450aa', screening: '#f0a6aa', assessment: '#875e8a' })[t] || '#6779e3'

  const loading = patientsLoading || referenceLoading
  // No longer require clinic brand selection - it's now optional
  const showSelectBrandMessage = false

  // Check if any advanced filter is active
  const hasAdvancedFilters =
    filters.monitored !== null && filters.monitored !== '' ||
    filters.monitoringScope ||
    filters.monitoringPolicy ||
    filters.monitoringEventModes?.length > 0 ||
    filters.monitoringEventTypes?.length > 0 ||
    filters.monitoringActions?.length > 0 ||
    filters.includeImportId ||
    filters.includeUhId

  // Render tile card
  const renderTileCard = (patient) => {
    const age = getPatientAge(patient.dateOfBirth)
    const contact = getContactInfo(patient)
    const isSelected = selectedPatient?.id === patient.id

    return (
      <PatientCard key={patient.id} selected={isSelected} onClick={() => handlePatientClick(patient)}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              backgroundColor: getAvatarColor(patient.gender),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
              border: `2px solid ${getIconColor(patient.gender)}40`,
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 32, color: getIconColor(patient.gender) }} />
            {age && (
              <Typography
                sx={{
                  position: 'absolute',
                  bottom: -6,
                  fontSize: '11px',
                  fontWeight: 700,
                  color: 'white',
                  backgroundColor: getIconColor(patient.gender),
                  borderRadius: '5px',
                  padding: '2px 5px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  border: '1.5px solid white',
                }}
              >
                {age}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
              {patient.name || 'Unknown'}
            </Typography>
            {contact.type !== 'none' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                {contact.type === 'mobile' ? <PhoneIcon sx={{ fontSize: 12, color: '#64748b' }} /> : <EmailIcon sx={{ fontSize: 12, color: '#64748b' }} />}
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {contact.value}
                </Typography>
              </Box>
            )}
            {patient.patientNumber && (
              <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace' }}>
                #{patient.patientNumber}
              </Typography>
            )}
            {/* Event Type Icons */}
            {patient.eventTypes?.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, mb: 0.25 }}>
                {patient.eventTypes.includes('consultation') && (
                  <Tooltip title="Consultation Patient">
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 22, 
                      height: 22, 
                      borderRadius: '6px', 
                      backgroundColor: 'rgba(103, 121, 227, 0.12)',
                    }}>
                      <MedicalServicesIcon sx={{ fontSize: 14, color: '#6779e3' }} />
                    </Box>
                  </Tooltip>
                )}
                {(patient.eventTypes.includes('therapy-session') || patient.eventTypes.includes('therapySession')) && (
                  <Tooltip title="Therapy Session Patient">
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 22, 
                      height: 22, 
                      borderRadius: '6px', 
                      backgroundColor: 'rgba(116, 80, 170, 0.12)',
                    }}>
                      <PsychologyIcon sx={{ fontSize: 14, color: '#7450aa' }} />
                    </Box>
                  </Tooltip>
                )}
                {(patient.eventTypes.includes('screening') || patient.eventTypes.includes('assessment')) && (
                  <Tooltip title="Screening/Assessment Patient">
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 22, 
                      height: 22, 
                      borderRadius: '6px', 
                      backgroundColor: 'rgba(135, 94, 138, 0.12)',
                    }}>
                      <AssignmentIcon sx={{ fontSize: 14, color: '#875e8a' }} />
                    </Box>
                  </Tooltip>
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {patient.gender && (
                <Chip
                  label={getGenderLabel(patient.gender)}
                  size="small"
                  sx={{ height: 18, fontSize: '10px', backgroundColor: getAvatarColor(patient.gender), color: getIconColor(patient.gender) }}
                />
              )}
              {hasMonitoringPermission && (
                <Tooltip title={patient.monitored ? 'Manage Monitoring' : 'Enable Monitoring'}>
                  <Chip
                    icon={<MonitorHeartIcon sx={{ fontSize: '12px !important' }} />}
                    label={patient.monitored ? 'Monitored' : 'Monitor'}
                    size="small"
                    onClick={(e) => handleMonitoringClick(e, patient)}
                    sx={{
                      height: 18,
                      fontSize: '10px',
                      cursor: 'pointer',
                      backgroundColor: patient.monitored ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                      color: patient.monitored ? '#10b981' : '#64748b',
                      '&:hover': {
                        backgroundColor: patient.monitored ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                      },
                    }}
                  />
                </Tooltip>
              )}
              {hasEngagementPermission && patient.waitingScreenEngagement && (
                <Tooltip title="Engagement Enabled - Click to manage">
                  <Chip
                    icon={<SportsEsportsIcon sx={{ fontSize: '12px !important' }} />}
                    label="Engaged"
                    size="small"
                      onClick={(e) => {
                      e.stopPropagation()
                      handlePatientClick(patient)
                      // Switch to engagement tab after a brief delay
                      setTimeout(() => setDrawerTab(DRAWER_TABS.ENGAGEMENT), 100)
                    }}
                    sx={{
                      height: 18,
                      fontSize: '10px',
                      cursor: 'pointer',
                      backgroundColor: 'rgba(156, 39, 176, 0.15)',
                      color: '#9c27b0',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: 'rgba(156, 39, 176, 0.25)',
                      },
                    }}
                  />
                </Tooltip>
              )}
              {patient.tags?.length > 0 && (
                <Chip
                  label={`${patient.tags.length} tag${patient.tags.length > 1 ? 's' : ''}`}
                  size="small"
                  sx={{ height: 18, fontSize: '10px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}
                />
              )}
            </Box>
          </Box>
        </Box>
        {/* Clinic Brand Strip */}
        {(patient.brandData?.brandName || patient.brandData?.title) && (
          <Box
            sx={{
              mt: 1.5,
              mx: -2,
              px: 2,
              py: 0.75,
              backgroundColor: 'rgba(100, 116, 139, 0.06)',
              borderTop: '1px solid rgba(226, 232, 240, 0.6)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <BusinessIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            <Typography
              variant="caption"
              sx={{
                color: '#64748b',
                fontSize: '11px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {patient.brandData?.brandName || patient.brandData?.title}
            </Typography>
          </Box>
        )}
      </PatientCard>
    )
  }

  // Render list view
  const renderListView = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Patient</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Contact</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>IDs</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Clinic</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Registered</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Modified</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {patients.map((patient) => {
            const age = getPatientAge(patient.dateOfBirth)
            const isSelected = selectedPatient?.id === patient.id
            return (
              <StyledTableRow key={patient.id} selected={isSelected} onClick={() => handlePatientClick(patient)}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        backgroundColor: getAvatarColor(patient.gender),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <AccountCircleIcon sx={{ fontSize: 22, color: getIconColor(patient.gender) }} />
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {patient.name || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {age ? `${age}y` : ''} {patient.gender ? `• ${getGenderLabel(patient.gender)}` : ''}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {patient.mobile || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {patient.email || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                    {patient.patientNumber || '-'}
                  </Typography>
                  {patient.uhId && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      UH: {patient.uhId}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    {patient.brandData?.brandName || patient.brandData?.title || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {patient.clinicData?.title || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Chip label={patient.status || 'unknown'} size="small" sx={{ height: 20, fontSize: '10px' }} />
                    {hasMonitoringPermission && (
                      <Tooltip title={patient.monitored ? 'Manage Monitoring' : 'Enable Monitoring'}>
                        <Chip
                          icon={<MonitorHeartIcon sx={{ fontSize: '10px !important' }} />}
                          label={patient.monitored ? 'Monitored' : 'Monitor'}
                          size="small"
                          onClick={(e) => handleMonitoringClick(e, patient)}
                          sx={{
                            height: 20,
                            fontSize: '10px',
                            cursor: 'pointer',
                            backgroundColor: patient.monitored ? 'rgba(16, 185, 129, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                            color: patient.monitored ? '#10b981' : '#64748b',
                            '&:hover': {
                              backgroundColor: patient.monitored ? 'rgba(16, 185, 129, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                            },
                          }}
                        />
                      </Tooltip>
                    )}
                    {hasEngagementPermission && patient.waitingScreenEngagement && (
                      <Tooltip title="Engagement Enabled - Click to manage">
                        <Chip
                          icon={<SportsEsportsIcon sx={{ fontSize: '10px !important' }} />}
                          label="Engaged"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePatientClick(patient)
                            setTimeout(() => setDrawerTab(DRAWER_TABS.ENGAGEMENT), 100)
                          }}
                          sx={{
                            height: 20,
                            fontSize: '10px',
                            cursor: 'pointer',
                            backgroundColor: 'rgba(156, 39, 176, 0.15)',
                            color: '#9c27b0',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: 'rgba(156, 39, 176, 0.25)',
                            },
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{formatDateShort(patient.registeredOn)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{formatDateShort(patient.modifiedTime)}</Typography>
                </TableCell>
              </StyledTableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )

  // Render patient details drawer (keeping existing comprehensive drawer)
  const renderPatientDetailsDrawer = () => {
    if (!selectedPatient) return null
    const patient = selectedPatient
    const age = getPatientAge(patient.dateOfBirth)
    const patientContactEventTypes = getPatientSupportContactEventTypes(patient)
    const patientContactEventTypesCsv =
      patientContactEventTypes.length > 0 ? patientContactEventTypes.join(',') : undefined

    return (
      <StyledDrawer anchor="right" open={drawerOpen} onClose={() => { setDrawerOpen(false); setPatientEditDialogOpen(false) }}>
        <Box sx={{ height: '100vh', maxHeight: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ee 100%)', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>Patient Details</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {hasMonitoringPermission && (
                  <Tooltip title={patient.monitored ? 'Manage Monitoring' : 'Enable Monitoring'}>
                    <Button
                      size="small"
                      startIcon={<MonitorHeartIcon />}
                      variant={patient.monitored ? 'contained' : 'outlined'}
                      color={patient.monitored ? 'success' : 'inherit'}
                      onClick={() => handleMonitoringClick(null, patient)}
                    >
                      {patient.monitored ? 'Monitored' : 'Monitor'}
                    </Button>
                  </Tooltip>
                )}
                {hasEngagementPermission && (
                  <Tooltip title="Configure Waiting Engagement">
                    <Button
                      size="small"
                      startIcon={<SportsEsportsIcon />}
                      variant="outlined"
                      onClick={() => handleEngagementClick(null, patient)}
                      sx={{
                        borderColor: '#8b5cf6',
                        color: '#8b5cf6',
                        '&:hover': {
                          borderColor: '#7c3aed',
                          backgroundColor: 'rgba(139, 92, 246, 0.04)',
                        },
                      }}
                    >
                      Engagement
                    </Button>
                  </Tooltip>
                )}
                <Button size="small" startIcon={<EditIcon />} variant="outlined" onClick={handleOpenPatientEdit}>Edit</Button>
                <IconButton size="small" onClick={() => { setDrawerOpen(false); setPatientEditDialogOpen(false) }}><CloseIcon /></IconButton>
              </Box>
            </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '14px', backgroundColor: getAvatarColor(patient.gender), display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${getIconColor(patient.gender)}40` }}>
              <AccountCircleIcon sx={{ fontSize: 32, color: getIconColor(patient.gender) }} />
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600}>{patient.name || 'Unknown'}</Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                  {patient.patientNumber && <Chip label={`#${patient.patientNumber}`} size="small" sx={{ height: 22, fontFamily: 'monospace', fontSize: '11px' }} />}
                  {age && <Chip label={`${age}y`} size="small" sx={{ height: 22, backgroundColor: getAvatarColor(patient.gender), color: getIconColor(patient.gender) }} />}
                  {patient.gender && <Chip label={getGenderLabel(patient.gender)} size="small" sx={{ height: 22, backgroundColor: getAvatarColor(patient.gender), color: getIconColor(patient.gender) }} />}
                  {patient.status && <Chip label={patient.status} size="small" sx={{ height: 22 }} />}
                  {patient.isTestPatient && <Chip label="TEST" size="small" color="warning" sx={{ height: 22 }} />}
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <SupportContactActions
              key={`patient-contact-${patient?.id || 'unknown'}-${patientContactEventTypesCsv || 'default'}`}
              patientId={patient?.id}
              practitionerId={patient?.mentorId || undefined}
              clinicBrandId={patient?.clinicBrandId || undefined}
              clinicBrandName={patient?.brandData?.title || undefined}
              clinicId={patient?.clinicId || undefined}
              targetRole="patient"
              eventType={patientContactEventTypes.length === 1 ? patientContactEventTypes[0] : undefined}
              eventTypesCsv={patientContactEventTypesCsv}
              eventTypeOptions={patientContactEventTypes.length > 0
                ? EVENT_TYPE_OPTIONS.filter((option) => patientContactEventTypes.includes(option.value))
                : EVENT_TYPE_OPTIONS}
              uiSurface="support-patient-management-drawer"
              title="Patient Contact"
            />
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs value={drawerTab} onChange={(e, v) => setDrawerTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tab value={DRAWER_TABS.DETAILS} label="Details" icon={<InfoIcon sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
            <Tab value={DRAWER_TABS.HISTORY} label="History" icon={<HistoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
            <Tab value={DRAWER_TABS.PACKS} label="Pack History" icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 48, textTransform: 'none' }} />
            {hasEngagementPermission && (
              <Tab 
                value={DRAWER_TABS.ENGAGEMENT}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Engagement
                    {patient?.waitingScreenEngagement && (
                      <Chip 
                        label="ON" 
                        size="small" 
                        sx={{ 
                          height: 16, 
                          fontSize: '9px', 
                          fontWeight: 700,
                          backgroundColor: alpha('#9c27b0', 0.15),
                          color: '#9c27b0',
                          ml: 0.5,
                        }} 
                      />
                    )}
                  </Box>
                } 
                icon={<SportsEsportsIcon sx={{ fontSize: 18 }} />} 
                iconPosition="start" 
                sx={{ minHeight: 48, textTransform: 'none' }} 
              />
            )}
          </Tabs>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2.5, minHeight: 0 }}>
            {drawerTab === DRAWER_TABS.DETAILS && (
              <>
                {/* Identity & IDs */}
                <Accordion defaultExpanded sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BadgeIcon sx={{ color: '#6779e3', fontSize: 20 }} /><Typography fontWeight={600}>Identity & IDs</Typography></Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6}><CopyableField label="Patient ID" value={patient.id} monospace /></Grid>
                      <Grid item xs={6}><CopyableField label="Patient Number" value={patient.patientNumber} monospace /></Grid>
                      <Grid item xs={6}><CopyableField label="UH ID" value={patient.uhId} monospace /></Grid>
                      <Grid item xs={6}><CopyableField label="User ID" value={patient.userId} monospace /></Grid>
                      <Grid item xs={6}><CopyableField label="Slug" value={patient.slug} monospace /></Grid>
                      <Grid item xs={6}><CopyableField label="Mentor ID" value={patient.mentorId} monospace /></Grid>
                      <Grid item xs={6}><CopyableField label="Clinic Brand ID" value={patient.clinicBrandId} monospace /></Grid>
                      <Grid item xs={6}><CopyableField label="Clinic ID" value={patient.clinicId} monospace /></Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Contact Information */}
                <Accordion defaultExpanded sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ContactsIcon sx={{ color: '#6779e3', fontSize: 20 }} /><Typography fontWeight={600}>Contact Information</Typography></Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6}><CopyableField label="Mobile" value={patient.mobile} /></Grid>
                      <Grid item xs={6}><CopyableField label="Email" value={patient.email} /></Grid>
                      <Grid item xs={6}><DetailLabel>Alt Mobiles</DetailLabel><DetailValue>{patient.altMobiles?.join(', ') || '-'}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Alt Emails</DetailLabel><DetailValue>{patient.altEmails?.join(', ') || '-'}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Languages</DetailLabel><DetailValue>{patient.languages?.join(', ') || '-'}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Default Comm. Language</DetailLabel><DetailValue>{patient.defaultCommunicationLanguage || '-'}</DetailValue></Grid>
                      <Grid item xs={12}><DetailLabel>Communication Types</DetailLabel><Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>{patient.communicationTypes?.map((t) => <Chip key={t} label={t} size="small" sx={{ height: 22 }} />) || '-'}</Box></Grid>
                      <Grid item xs={6}><DetailLabel>Communicate to Attendant</DetailLabel><DetailValue>{patient.communicationToAttendant ? 'Yes' : 'No'}</DetailValue></Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Location */}
                {patient.location && (
                  <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><HomeIcon sx={{ color: '#6779e3', fontSize: 20 }} /><Typography fontWeight={600}>Location</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}><DetailLabel>Address</DetailLabel><DetailValue>{patient.location.address || '-'}</DetailValue></Grid>
                        <Grid item xs={4}><DetailLabel>City</DetailLabel><DetailValue>{patient.location.city || '-'}</DetailValue></Grid>
                        <Grid item xs={4}><DetailLabel>State</DetailLabel><DetailValue>{patient.location.state || '-'}</DetailValue></Grid>
                        <Grid item xs={4}><DetailLabel>Pincode</DetailLabel><DetailValue>{patient.location.pincode || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Country</DetailLabel><DetailValue>{patient.location.country || '-'}</DetailValue></Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Registration & Lifecycle */}
                <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CalendarTodayIcon sx={{ color: '#6779e3', fontSize: 20 }} /><Typography fontWeight={600}>Registration & Lifecycle</Typography></Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6}><DetailLabel>Date of Birth</DetailLabel><DetailValue>{formatDateShort(patient.dateOfBirth)}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Gender</DetailLabel><DetailValue>{getGenderLabel(patient.gender) || '-'}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Registered On</DetailLabel><DetailValue>{formatDate(patient.registeredOn)}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Status</DetailLabel><DetailValue>{patient.status || '-'}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Login Type</DetailLabel><DetailValue>{patient.loginType || '-'}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Referred By</DetailLabel><DetailValue>{patient.referredBy || '-'}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Created Time</DetailLabel><DetailValue>{formatDate(patient.createdTime)}</DetailValue></Grid>
                      <Grid item xs={6}><DetailLabel>Modified Time</DetailLabel><DetailValue>{formatDate(patient.modifiedTime)}</DetailValue></Grid>
                      <Grid item xs={6}><CopyableField label="Created By" value={patient.createdBy} monospace /></Grid>
                      <Grid item xs={6}><CopyableField label="Modified By" value={patient.modifiedBy} monospace /></Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                {/* Tags */}
                {patient.tags?.length > 0 && (
                  <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocalOfferIcon sx={{ color: '#f59e0b', fontSize: 20 }} /><Typography fontWeight={600}>Tags ({patient.tags.length})</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1.5}>
                        {patient.tags.map((tag, idx) => (
                          <Paper key={idx} sx={{ p: 1.5, backgroundColor: `${tag.color || '#f59e0b'}10`, border: `1px solid ${tag.color || '#f59e0b'}40` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Chip label={tag.label || tag.tagCode} size="small" sx={{ backgroundColor: tag.color || '#f59e0b', color: 'white', fontWeight: 600 }} />
                              <Typography variant="caption" color="text.secondary">{tag.source}</Typography>
                            </Box>
                            {tag.note && <Typography variant="body2">{tag.note}</Typography>}
                            <Typography variant="caption" color="text.secondary">Added: {formatDate(tag.createdTime)}</Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Attendants */}
                {patient.attendants?.length > 0 && (
                  <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FamilyRestroomIcon sx={{ color: '#7450aa', fontSize: 20 }} /><Typography fontWeight={600}>Attendants ({patient.attendants.length})</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={1}>
                        {patient.attendants.map((att, idx) => (
                          <Paper key={idx} sx={{ p: 1.5, backgroundColor: 'rgba(116, 80, 170, 0.05)', border: '1px solid rgba(116, 80, 170, 0.2)' }}>
                            <Typography fontWeight={600}>{att.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{att.title || att.relationship}</Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Primary Patient Profile */}
                {patient.primaryPatientProfile?.patientId && (
                  <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonIcon sx={{ color: '#7450aa', fontSize: 20 }} /><Typography fontWeight={600}>Primary Patient (Dependent Of)</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={6}><CopyableField label="Primary Patient ID" value={patient.primaryPatientProfile.patientId} monospace /></Grid>
                        <Grid item xs={6}><DetailLabel>Relationship</DetailLabel><DetailValue>{patient.primaryPatientProfile.dependentRelation || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Mobile</DetailLabel><DetailValue>{patient.primaryPatientProfile.mobile || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Email</DetailLabel><DetailValue>{patient.primaryPatientProfile.email || '-'}</DetailValue></Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Monitoring Settings */}
                {patient.monitored && (
                  <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><MonitorHeartIcon sx={{ color: '#10b981', fontSize: 20 }} /><Typography fontWeight={600}>Monitoring Settings</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={6}><DetailLabel>Monitored</DetailLabel><DetailValue>{patient.monitored ? 'Yes' : 'No'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Scope</DetailLabel><DetailValue>{patient.monitoringSettings?.scope || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Policy</DetailLabel><DetailValue>{patient.monitoringSettings?.policy || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Event Modes</DetailLabel><DetailValue>{patient.monitoringSettings?.eventModes?.join(', ') || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Event Types</DetailLabel><DetailValue>{patient.monitoringSettings?.eventTypes?.join(', ') || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Actions</DetailLabel><DetailValue>{patient.monitoringSettings?.actions?.join(', ') || '-'}</DetailValue></Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Import Data */}
                {patient.importData && Object.keys(patient.importData).length > 0 && (
                  <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><UploadFileIcon sx={{ color: '#6779e3', fontSize: 20 }} /><Typography fontWeight={600}>Import Data</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={6}><DetailLabel>Imported From</DetailLabel><DetailValue>{patient.importData.importedFrom || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><CopyableField label="Import ID" value={patient.importData.importedFromId} monospace /></Grid>
                        <Grid item xs={6}><DetailLabel>Import Patient Number</DetailLabel><DetailValue>{patient.importData.importedFromPatientNumber || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Imported On</DetailLabel><DetailValue>{formatDate(patient.importData.importedOn)}</DetailValue></Grid>
                        <Grid item xs={6}><CopyableField label="Imported By" value={patient.importData.importedBy} monospace /></Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Merge Data */}
                {patient.mergeData?.merged && (
                  <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><MergeIcon sx={{ color: '#f59e0b', fontSize: 20 }} /><Typography fontWeight={600}>Merge Data</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={6}><CopyableField label="Merged From" value={patient.mergeData.mergedFrom} monospace /></Grid>
                        <Grid item xs={6}><CopyableField label="Merged To" value={patient.mergeData.mergedTo} monospace /></Grid>
                        <Grid item xs={6}><DetailLabel>Merge Reason</DetailLabel><DetailValue>{patient.mergeData.mergedReason || '-'}</DetailValue></Grid>
                        <Grid item xs={6}><DetailLabel>Merged Time</DetailLabel><DetailValue>{formatDate(patient.mergeData.mergedTime)}</DetailValue></Grid>
                        <Grid item xs={6}><CopyableField label="Merged By" value={patient.mergeData.mergedBy} monospace /></Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Notes */}
                {patient.note && (
                  <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><NotesIcon sx={{ color: '#6779e3', fontSize: 20 }} /><Typography fontWeight={600}>Notes</Typography></Box>
                    </AccordionSummary>
                    <AccordionDetails><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{patient.note}</Typography></AccordionDetails>
                  </Accordion>
                )}

                {/* System Flags */}
                <Accordion sx={{ mb: 1.5, boxShadow: 'none', border: '1px solid #e2e8f0', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SettingsIcon sx={{ color: '#64748b', fontSize: 20 }} /><Typography fontWeight={600}>System Flags</Typography></Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={4}><DetailLabel>Enabled</DetailLabel><DetailValue>{patient.enabled ? 'Yes' : 'No'}</DetailValue></Grid>
                      <Grid item xs={4}><DetailLabel>Deleted</DetailLabel><DetailValue>{patient.deleted ? 'Yes' : 'No'}</DetailValue></Grid>
                      <Grid item xs={4}><DetailLabel>Anonymous</DetailLabel><DetailValue>{patient.anonymous ? 'Yes' : 'No'}</DetailValue></Grid>
                      <Grid item xs={4}><DetailLabel>Test Patient</DetailLabel><DetailValue>{patient.isTestPatient ? 'Yes' : 'No'}</DetailValue></Grid>
                      <Grid item xs={4}><DetailLabel>ID By User</DetailLabel><DetailValue>{patient.idByUser ? 'Yes' : 'No'}</DetailValue></Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            {drawerTab === DRAWER_TABS.HISTORY && (
              <Box>
                {/* Event Type Filter Toggles */}
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Filter by Event Type
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {EVENT_TYPE_OPTIONS.map((option) => {
                      const isSelected = visitHistoryEventTypes.includes(option.value)
                      const eventColor = getEventTypeColor(option.value)
                      return (
                        <Chip
                          key={option.value}
                          label={option.label}
                          size="small"
                          onClick={() => {
                            const newTypes = isSelected
                              ? visitHistoryEventTypes.filter((t) => t !== option.value)
                              : [...visitHistoryEventTypes, option.value]
                            handleVisitHistoryFilterChange(newTypes)
                          }}
                          sx={{
                            height: 26,
                            fontSize: '11px',
                            fontWeight: 500,
                            cursor: 'pointer',
                            backgroundColor: isSelected ? `${eventColor}20` : 'white',
                            color: isSelected ? eventColor : '#64748b',
                            border: `1.5px solid ${isSelected ? eventColor : '#e2e8f0'}`,
                            '&:hover': {
                              backgroundColor: isSelected ? `${eventColor}30` : '#f1f5f9',
                              borderColor: eventColor,
                            },
                          }}
                        />
                      )
                    })}
                    {visitHistoryEventTypes.length > 0 && (
                      <Chip
                        label="Clear All"
                        size="small"
                        onClick={() => handleVisitHistoryFilterChange([])}
                        sx={{
                          height: 26,
                          fontSize: '11px',
                          cursor: 'pointer',
                          backgroundColor: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          border: '1.5px solid #ef4444',
                          '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.2)' },
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {visitHistoryLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>
                ) : visitHistory.length > 0 ? (
                  <Box sx={{ position: 'relative', pl: 3 }}>
                    <Box sx={{ position: 'absolute', left: '11px', top: 0, bottom: 0, width: '2px', backgroundColor: 'rgba(103, 121, 227, 0.2)' }} />
                    {visitHistory.map((visit, idx) => {
                      const eventTypeColor = getEventTypeColor(visit.eventType)
                      const eventTypeName = displayNamesOfEventTypes[visit.eventType] || visit.eventType || 'Session'
                      const visitDate = dayjs.utc(visit.startTime).local()
                      const visitEndDate = visit.endTime ? dayjs.utc(visit.endTime).local() : null
                      
                      // Get session status colors using the same scheme as dayCalendar
                      const slotColors = getBookedSlotStripColor(visit, false, true)
                      
                      // Session status display
                      const sessionStatus = visit.sessionStatus
                      const sessionStatusLabel = BOOKING_SESSION_STATUS_MAP[sessionStatus]?.title || sessionStatus || 'Unknown'
                      const isCompleted = visit.markedCompleted
                      const isCancelled = visit.status === 'cancelled'
                      const isInProgress = sessionStatus === BOOKING_SESSION_STATUS.IN_PROGRESS
                      
                      // Platform info
                      const isOnline = visit.meetingSrc === SESSION_PLATFORM_ONLINE
                      const isOffline = visit.meetingSrc === SESSION_PLATFORM_OFFLINE
                      
                      // Visit number
                      const visitNumber = visit.patientMeetingSeqNumber
                      
                      // Combined visit label (e.g., "1st Consultation Visit")
                      const visitLabel = visitNumber 
                        ? `${visitNumber}${getOrdinalSuffixForSuperScript(visitNumber)} ${eventTypeName}`
                        : eventTypeName
                      
                      // Patient info from visit
                      const patientNumber = visit.patientInfo?.patientNumber || visit.patientNumber
                      const patientAge = visit.patientInfo?.dateOfBirth 
                        ? getAgeFromDobWithTime(visit.patientInfo.dateOfBirth) 
                        : null
                      const patientGender = visit.patientInfo?.gender
                      const genderLabel = patientGender 
                        ? GENDER_LIST_ONBOARDING?.find(g => g.value === patientGender)?.label || patientGender 
                        : null
                      
                      // Payment info
                      const paymentStatus = visit.paymentStatus
                      const feePaid = visit.feePaid
                      const currency = (visit.currency || 'inr').toUpperCase()
                      const paymentMode = visit.feePaymentMode
                      const paymentModeLabel = OFFLINE_PAYMENT_METHODS_LIST?.find(m => m.id === paymentMode)?.title || paymentMode
                      
                      // Clinic info
                      const clinicBrandName = visit.clinicBrandData?.brandName || visit.clinicBrandName
                      const clinicLocationName = visit.clinicLocation?.title || visit.clinicName
                      
                      // Determine session status color based on actual status
                      const getSessionStatusStyle = () => {
                        if (isCompleted) {
                          return { bg: '#D1FAE5', color: '#047857', border: '#10B981' }
                        }
                        if (isCancelled) {
                          return { bg: '#FEE2E2', color: '#DC2626', border: '#EF4444' }
                        }
                        if (isInProgress) {
                          return { bg: '#FEF3C7', color: '#92400E', border: '#F59E0B' }
                        }
                        return { bg: '#E8E9EA', color: '#4B5563', border: '#6366F1' }
                      }
                      
                      // Status (booking status like 'booked', 'cancelled', etc.)
                      const bookingStatus = visit.status
                      const getBookingStatusStyle = () => {
                        if (bookingStatus === 'cancelled') {
                          return { bg: '#FEE2E2', color: '#DC2626', border: '#EF4444' }
                        }
                        if (bookingStatus === 'booked') {
                          return { bg: '#DBEAFE', color: '#1D4ED8', border: '#3B82F6' }
                        }
                        if (bookingStatus === 'booking-initiated') {
                          return { bg: '#FFF4E6', color: '#C2410C', border: '#FB923C' }
                        }
                        return { bg: '#F3F4F6', color: '#6B7280', border: '#9CA3AF' }
                      }
                      
                      const sessionStatusStyle = getSessionStatusStyle()
                      const bookingStatusStyle = getBookingStatusStyle()
                      
                      // Copy to clipboard helper
                      const copyToClipboard = (text, label) => {
                        navigator.clipboard.writeText(text)
                        toast.success(`${label} copied!`)
                      }
                      
                      return (
                        <Box key={visit.id || idx} sx={{ position: 'relative', mb: 2 }}>
                          <Box sx={{ 
                            position: 'absolute', 
                            left: '-20px', 
                            top: '6px', 
                            width: '20px', 
                            height: '20px', 
                            borderRadius: '50%', 
                            backgroundColor: slotColors?.borderColor || eventTypeColor, 
                            border: '2px solid white', 
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
                          }} />
                          <Paper sx={{ 
                            p: 2, 
                            borderRadius: 2, 
                            border: `2px solid ${slotColors?.borderColor || eventTypeColor}40`, 
                            backgroundColor: slotColors?.background || `${eventTypeColor}05`,
                          }}>
                            {/* Header: Combined Visit Label + Status chips */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', flex: 1 }}>
                                {/* Combined Event Type + Visit Number */}
                                <Chip 
                                  label={visitLabel}
                                  size="medium" 
                                  sx={{ 
                                    background: `linear-gradient(135deg, ${eventTypeColor}20 0%, ${eventTypeColor}10 100%)`,
                                    color: eventTypeColor, 
                                    fontWeight: 700, 
                                    fontSize: '13px',
                                    height: 28,
                                    border: `1px solid ${eventTypeColor}40`,
                                  }} 
                                />
                                
                                {/* Platform indicator */}
                                <Chip
                                  icon={isOnline ? <VideoCallIcon sx={{ fontSize: 16 }} /> : <PersonPinCircleIcon sx={{ fontSize: 16 }} />}
                                  label={isOnline ? 'Video' : 'Offline'}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    backgroundColor: isOnline ? 'rgba(99, 102, 241, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                    color: isOnline ? '#6366f1' : '#f59e0b',
                                    '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                                  }}
                                />
                              </Box>
                              
                              {/* Status chips - right aligned */}
                              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                <Chip
                                  label={isCompleted ? 'Completed' : isCancelled ? 'Cancelled' : sessionStatusLabel}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    backgroundColor: sessionStatusStyle.bg,
                                    color: sessionStatusStyle.color,
                                  }}
                                />
                                {bookingStatus === 'booking-initiated' && (
                                  <Chip label="Pay Pending" size="small" sx={{ height: 24, fontSize: '11px', fontWeight: 500, backgroundColor: '#FFF4E6', color: '#C2410C' }} />
                                )}
                              </Box>
                            </Box>

                            {/* Two-column layout for main info */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                              {/* Left column: Date/Time & Practitioner */}
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                  <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" fontWeight={600} sx={{ color: slotColors?.fontColor }}>
                                    {visitDate.format('DD MMM YYYY')}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                                  <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="body2" color="text.secondary">
                                    {visitDate.format('h:mm A')}{visitEndDate && ` - ${visitEndDate.format('h:mm A')}`}
                                  </Typography>
                                </Box>
                                {visit.mentorName && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <PersonPinIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {visit.mentorName}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>

                              {/* Right column: IDs & Payment */}
                              <Box>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
                                  {patientNumber && (
                                    <Chip label={`#${patientNumber}`} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, backgroundColor: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }} />
                                  )}
                                  {patientAge && genderLabel && (
                                    <Chip label={`${patientAge}Y / ${genderLabel.charAt(0)}`} size="small" sx={{ height: 22, fontSize: '11px', backgroundColor: 'rgba(236, 72, 153, 0.1)', color: '#ec4899' }} />
                                  )}
                                  {visit.appointmentNumber && (
                                    <Chip label={`Appt #${visit.appointmentNumber}`} size="small" sx={{ height: 22, fontSize: '11px', backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }} />
                                  )}
                                </Box>
                                {feePaid && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <PaymentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {currency} {feePaid}
                                      {paymentModeLabel && <span style={{ opacity: 0.7 }}> ({paymentModeLabel})</span>}
                                    </Typography>
                                    {paymentStatus && (
                                      <Chip
                                        label={paymentStatus === BOOKING_PAYMENT_STATUSES?.COMPLETED ? '✓ Paid' : '⏳ Pending'}
                                        size="small"
                                        sx={{ height: 20, fontSize: '10px', fontWeight: 600, backgroundColor: paymentStatus === BOOKING_PAYMENT_STATUSES?.COMPLETED ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)', color: paymentStatus === BOOKING_PAYMENT_STATUSES?.COMPLETED ? '#10b981' : '#f59e0b' }}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </Box>

                            {/* Clinic & Room - single row if present */}
                            {(clinicBrandName || clinicLocationName || (isOffline && visit.chamberRoomName)) && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                                <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                {clinicBrandName && (
                                  <Typography variant="body2" color="text.secondary" fontWeight={500}>{clinicBrandName}</Typography>
                                )}
                                {clinicBrandName && clinicLocationName && <Typography variant="body2" color="text.disabled">•</Typography>}
                                {clinicLocationName && (
                                  <Typography variant="body2" color="text.secondary">{clinicLocationName}</Typography>
                                )}
                                {isOffline && visit.chamberRoomName && (
                                  <>
                                    <Typography variant="body2" color="text.disabled">•</Typography>
                                    <MeetingRoomIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">{visit.chamberRoomName}</Typography>
                                  </>
                                )}
                              </Box>
                            )}

                            {/* Joining Links Button - for online sessions */}
                            {isOnline && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
                                onClick={() => handleOpenJoiningLinksDialog(visit)}
                                sx={{
                                  fontSize: '12px',
                                  py: 0.5,
                                  px: 1.5,
                                  mb: 1,
                                  borderColor: '#6366f1',
                                  color: '#6366f1',
                                  '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)', borderColor: '#6366f1' },
                                }}
                              >
                                Joining Links
                              </Button>
                            )}

                            {/* Invoice / Print Receipt */}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ReceiptIcon sx={{ fontSize: 16 }} />}
                              onClick={() => openReceiptDialog(visit)}
                              sx={{
                                fontSize: '12px',
                                py: 0.5,
                                px: 1.5,
                                mb: 1,
                                borderColor: '#6366f1',
                                color: '#6366f1',
                                '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.1)', borderColor: '#6366f1' },
                              }}
                            >
                              Invoice
                            </Button>

                            {/* Rebook button - only show if user has permission for this event type */}
                            {canRebookEventType(visit.eventType) && !isCancelled && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EventRepeatIcon sx={{ fontSize: 16 }} />}
                                onClick={(e) => handleRebookClick(e, visit)}
                                sx={{
                                  fontSize: '12px',
                                  py: 0.5,
                                  px: 1.5,
                                  borderColor: eventTypeColor,
                                  color: eventTypeColor,
                                  '&:hover': { backgroundColor: `${eventTypeColor}10`, borderColor: eventTypeColor },
                                }}
                              >
                                Rebook
                              </Button>
                            )}
                          </Paper>
                        </Box>
                      )
                    })}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, borderRadius: 2, backgroundColor: 'rgba(103, 121, 227, 0.02)', border: '1px dashed rgba(103, 121, 227, 0.2)' }}>
                    <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No visit history found</Typography>
                  </Box>
                )}
              </Box>
            )}

            {drawerTab === DRAWER_TABS.PACKS && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <SupportPatientPackRequestsPanel
                    patient={patient}
                    practitionerId={patient?.mentorId}
                    clinicBrandId={patient?.clinicBrandId}
                    clinicLocationId={patient?.clinicId}
                    clinicBrandOptions={clinicBrands}
                    clinicLocationOptions={clinicLocations}
                    practitionerOptions={mentors}
                    allowScopeSelection
                    scopeOptionsLoading={referenceLoading}
                    title="Pack Purchase Requests"
                  />
                </Box>

                {packHistoryLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : packHistory.length > 0 ? (
                  <Stack spacing={2}>
                    {packHistory.map((pack, idx) => {
                      const eventTypeColor = getEventTypeColor(pack?.eventTypeGroup)
                      const consumptions = Array.isArray(pack?.consumptions) ? pack.consumptions : []
                      return (
                        <Paper
                          key={pack?.id || idx}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${eventTypeColor}40`,
                            backgroundColor: `${eventTypeColor}08`,
                          }}
                        >
                          <Stack spacing={1.5}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              justifyContent="space-between"
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                            >
                              <Stack spacing={0.75}>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  <Chip
                                    size="small"
                                    label={pack?.eventTypeTitle || displayNamesOfEventTypes?.[pack?.eventTypeGroup] || 'Pack'}
                                    sx={{
                                      backgroundColor: `${eventTypeColor}22`,
                                      color: eventTypeColor,
                                      fontWeight: 700,
                                    }}
                                  />
                                  {pack?.status ? (
                                    <Chip
                                      size="small"
                                      label={pack.status}
                                      variant="outlined"
                                      sx={{ fontWeight: 600 }}
                                    />
                                  ) : null}
                                </Stack>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                  {pack?.title || 'Pack'}
                                </Typography>
                                {pack?.eligibilityLabel ? (
                                  <Typography variant="body2" color="text.secondary">
                                    {pack.eligibilityLabel}
                                  </Typography>
                                ) : null}
                              </Stack>
                              <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                                {pack?.usageLabel ? (
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                    {pack.usageLabel}
                                  </Typography>
                                ) : null}
                                {pack?.expiryLabel ? (
                                  <Typography variant="caption" color="text.secondary">
                                    {pack.expiryLabel}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} flexWrap="wrap" useFlexGap>
                              {pack?.activationTime ? (
                                <Chip
                                  icon={<CalendarTodayIcon sx={{ fontSize: 15 }} />}
                                  label={`Activated ${formatDate(pack.activationTime)}`}
                                  size="small"
                                  variant="outlined"
                                />
                              ) : null}
                              {typeof pack?.remainingUnits === 'number' ? (
                                <Chip
                                  icon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />}
                                  label={`${pack.remainingUnits} remaining`}
                                  size="small"
                                  variant="outlined"
                                />
                              ) : null}
                            </Stack>

                            <Divider />

                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                Consumption History
                              </Typography>
                              {consumptions.length > 0 ? (
                                <Stack spacing={1}>
                                  {consumptions.map((consumption) => (
                                    <Paper
                                      key={consumption?.id}
                                      variant="outlined"
                                      sx={{ p: 1.25, borderRadius: 2, backgroundColor: '#fff' }}
                                    >
                                      <Stack spacing={0.5}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                          {consumption?.eventTitle || consumption?.eventTypeTitle || 'Session consumed'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {formatDate(consumption?.consumedAt)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {[consumption?.practitionerName, consumption?.clinicBrandName, consumption?.clinicLocationName]
                                            .filter(Boolean)
                                            .join(' • ')}
                                        </Typography>
                                        {(typeof consumption?.beforeRemainingUnits === 'number' || typeof consumption?.afterRemainingUnits === 'number') ? (
                                          <Typography variant="caption" color="text.secondary">
                                            Remaining units: {consumption?.beforeRemainingUnits ?? '-'} {'->'} {consumption?.afterRemainingUnits ?? '-'}
                                          </Typography>
                                        ) : null}
                                      </Stack>
                                    </Paper>
                                  ))}
                                </Stack>
                              ) : (
                                <Box
                                  sx={{
                                    textAlign: 'center',
                                    py: 2.5,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                    border: '1px dashed rgba(148, 163, 184, 0.45)',
                                  }}
                                >
                                  <Typography variant="body2" color="text.secondary">
                                    No sessions have been consumed from this pack yet.
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Stack>
                        </Paper>
                      )
                    })}
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      py: 4,
                      borderRadius: 2,
                      backgroundColor: 'rgba(103, 121, 227, 0.02)',
                      border: '1px dashed rgba(103, 121, 227, 0.2)',
                    }}
                  >
                    <Inventory2OutlinedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      No pack purchases found for this patient.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Engagement Tab */}
            {drawerTab === DRAWER_TABS.ENGAGEMENT && hasEngagementPermission && (
              <EngagementTabContent 
                patient={patient} 
                onRefresh={() => fetchData()}
              />
            )}
          </Box>
        </Box>
      </StyledDrawer>
    )
  }

  return (
    <>
      <SupportHeader heading="Patient List" showBackButton />
      <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
        {/* Filters Section */}
        <FilterSection>
          {/* Primary Filters Header */}
          <FilterHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FilterListIcon sx={{ color: '#6779e3' }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Filters
              </Typography>
              {activeFilterCount > 0 && (
                <Chip
                  label={`${activeFilterCount} active`}
                  size="small"
                  sx={{ height: 22, fontSize: '11px', backgroundColor: alpha('#6779e3', 0.1), color: '#6779e3' }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {activeFilterCount > 0 && (
                <Button size="small" startIcon={<ClearAllIcon />} onClick={clearFilters} sx={{ textTransform: 'none', color: '#64748b' }}>
                  Clear All
                </Button>
              )}
              <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
                <ToggleButton value="tile">
                  <Tooltip title="Tile View"><ViewModuleIcon sx={{ fontSize: 20 }} /></Tooltip>
                </ToggleButton>
                <ToggleButton value="list">
                  <Tooltip title="List View"><ViewListIcon sx={{ fontSize: 20 }} /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </FilterHeader>

          {/* Primary Filters */}
          <FilterBody>
            <Grid container spacing={2}>
              {/* Search with options */}
              <Grid item xs={12} md={4}>
                <TextField
                  placeholder="Search name, mobile, patient number..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchValue && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchValue('')}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
                />
                {/* Search options */}
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={filters.includeImportId}
                        onChange={(e) => handleFilterChange('includeImportId', e.target.checked)}
                      />
                    }
                    label={<Typography variant="caption" color="text.secondary">Include Import ID</Typography>}
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={filters.includeUhId}
                        onChange={(e) => handleFilterChange('includeUhId', e.target.checked)}
                      />
                    }
                    label={<Typography variant="caption" color="text.secondary">Include UH ID</Typography>}
                    sx={{ m: 0 }}
                  />
                </Box>
              </Grid>

              {/* Clinic Brand */}
              <Grid item xs={6} md={2}>
                <CompactSelect size="small" fullWidth>
                  <InputLabel>Clinic Brand</InputLabel>
                  <Select
                    value={filters.clinicBrandId || ''}
                    label="Clinic Brand"
                    onChange={(e) => {
                      handleMultipleFilterChange({ clinicBrandId: e.target.value, clinicId: '' })
                    }}
                  >
                    <MenuItem value=""><em>All Brands</em></MenuItem>
                    {clinicBrands.map((b) => (
                      <MenuItem key={b.id} value={b.id}>{b.brandName || b.title}</MenuItem>
                    ))}
                  </Select>
                </CompactSelect>
              </Grid>

              {/* Clinic Location */}
              <Grid item xs={6} md={2}>
                <CompactSelect size="small" fullWidth disabled={!filters.clinicBrandId}>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={filters.clinicId || ''}
                    label="Location"
                    onChange={(e) => handleFilterChange('clinicId', e.target.value)}
                  >
                    <MenuItem value=""><em>All Locations</em></MenuItem>
                    {filteredClinicLocations.map((l) => (
                      <MenuItem key={l.id} value={l.id}>{l.title || l.name}</MenuItem>
                    ))}
                  </Select>
                </CompactSelect>
              </Grid>

              {/* Practitioner */}
              <Grid item xs={6} md={2}>
                <CompactSelect size="small" fullWidth>
                  <InputLabel>Practitioner</InputLabel>
                  <Select
                    value={filters.mentorId || ''}
                    label="Practitioner"
                    onChange={(e) => handleFilterChange('mentorId', e.target.value)}
                  >
                    <MenuItem value=""><em>All</em></MenuItem>
                    {mentors.map((m) => (
                      <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                    ))}
                  </Select>
                </CompactSelect>
              </Grid>

              {/* Event Types */}
              <Grid item xs={6} md={2}>
                <MultiSelectFilter
                  label="Event Types"
                  value={filters.eventTypes || []}
                  options={EVENT_TYPE_OPTIONS}
                  onChange={(val) => handleFilterChange('eventTypes', val)}
                />
              </Grid>
            </Grid>

            {/* Advanced Filters Toggle */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed #e2e8f0' }}>
              <Button
                size="small"
                startIcon={<TuneIcon />}
                endIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{ textTransform: 'none', color: hasAdvancedFilters ? '#6779e3' : '#64748b' }}
              >
                Advanced Filters
                {hasAdvancedFilters && (
                  <Chip label="Active" size="small" sx={{ ml: 1, height: 18, fontSize: '10px', backgroundColor: alpha('#6779e3', 0.1), color: '#6779e3' }} />
                )}
              </Button>
            </Box>

            {/* Advanced Filters Content */}
            <Collapse in={showAdvancedFilters}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  <MonitorHeartIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  MONITORING FILTERS
                </Typography>
                <Grid container spacing={2}>
                  {/* Monitored Status */}
                  <Grid item xs={6} md={2}>
                    <CompactSelect size="small" fullWidth>
                      <InputLabel>Monitored</InputLabel>
                      <Select
                        value={filters.monitored === null ? '' : String(filters.monitored)}
                        label="Monitored"
                        onChange={(e) => {
                          const val = e.target.value
                          handleFilterChange('monitored', val === '' ? null : val === 'true')
                        }}
                      >
                        {MONITORED_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </CompactSelect>
                  </Grid>

                  {/* Monitoring Scope */}
                  <Grid item xs={6} md={2}>
                    <CompactSelect size="small" fullWidth>
                      <InputLabel>Scope</InputLabel>
                      <Select
                        value={filters.monitoringScope || ''}
                        label="Scope"
                        onChange={(e) => handleFilterChange('monitoringScope', e.target.value)}
                      >
                        {MONITORING_SCOPE_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </CompactSelect>
                  </Grid>

                  {/* Monitoring Policy */}
                  <Grid item xs={6} md={2}>
                    <CompactSelect size="small" fullWidth>
                      <InputLabel>Policy</InputLabel>
                      <Select
                        value={filters.monitoringPolicy || ''}
                        label="Policy"
                        onChange={(e) => handleFilterChange('monitoringPolicy', e.target.value)}
                      >
                        {MONITORING_POLICY_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </CompactSelect>
                  </Grid>

                  {/* Monitoring Event Modes */}
                  <Grid item xs={6} md={2}>
                    <MultiSelectFilter
                      label="Event Modes"
                      value={filters.monitoringEventModes || []}
                      options={MONITORING_EVENT_MODES}
                      onChange={(val) => handleFilterChange('monitoringEventModes', val)}
                    />
                  </Grid>

                  {/* Monitoring Event Types */}
                  <Grid item xs={6} md={2}>
                    <MultiSelectFilter
                      label="Mon. Event Types"
                      value={filters.monitoringEventTypes || []}
                      options={EVENT_TYPE_OPTIONS}
                      onChange={(val) => handleFilterChange('monitoringEventTypes', val)}
                    />
                  </Grid>

                  {/* Monitoring Actions */}
                  <Grid item xs={6} md={2}>
                    <MultiSelectFilter
                      label="Actions"
                      value={filters.monitoringActions || []}
                      options={MONITORING_ACTIONS}
                      onChange={(val) => handleFilterChange('monitoringActions', val)}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </FilterBody>
        </FilterSection>

        {/* Content */}
        {showSelectBrandMessage ? (
          <Box sx={{ textAlign: 'center', py: 8, backgroundColor: 'rgba(103, 121, 227, 0.02)', borderRadius: 2, border: '1px dashed rgba(103, 121, 227, 0.2)' }}>
            <BusinessIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>Select a Clinic Brand</Typography>
            <Typography variant="body2" color="text.secondary">Please select a clinic brand to view patients</Typography>
          </Box>
        ) : loading && patients.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : patients.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, backgroundColor: 'rgba(103, 121, 227, 0.02)', borderRadius: 2, border: '1px dashed rgba(103, 121, 227, 0.2)' }}>
            <PersonIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No Patients Found</Typography>
            <Typography variant="body2" color="text.secondary">{searchValue ? `No patients match "${searchValue}"` : 'No patients found with the current filters'}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {patients.length} patient{patients.length !== 1 ? 's' : ''}
              </Typography>
              {loading && <CircularProgress size={20} />}
            </Box>
            {viewMode === 'tile' ? (
              <Grid container spacing={2}>
                {patients.map((p) => (
                  <Grid 
                    item 
                    xs={12} 
                    sm={patients.length === 1 ? 12 : 6} 
                    md={patients.length === 1 ? 6 : 4} 
                    lg={patients.length === 1 ? 4 : 3} 
                    key={p.id}
                    sx={{ display: 'flex' }}
                  >
                    {renderTileCard(p)}
                  </Grid>
                ))}
              </Grid>
            ) : (
              renderListView()
            )}
            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={(e, p) => handlePageChange(p)}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Box>
      {renderPatientDetailsDrawer()}

      <Dialog
        open={patientEditDialogOpen}
        onClose={() => setPatientEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        {selectedPatient && (
          <PatientForm
            newPatient={false}
            patientId={selectedPatient?.id}
            clinicId={selectedPatient?.clinicId}
            clinicBrandId={selectedPatient?.clinicBrandId}
            clinicBrandData={selectedPatient?.brandData || undefined}
            mentorProfileData={selectedPatient?.mentorId ? { id: selectedPatient.mentorId } : undefined}
            importedFromItems={importedFromItems}
            patientSearchApiSignature="/api/secure/cc/support/patient/search"
            patientUpdateApiSignature="/api/secure/cc/support/patient/update"
            initialEditMode={true}
            onCancel={() => setPatientEditDialogOpen(false)}
            onSaveSuccess={handlePatientEditSaveSuccess}
          />
        )}
      </Dialog>

      {/* Patient Monitoring Dialog */}
      <PatientMonitoringDialog
        open={monitoringDialogOpen}
        onClose={() => {
          setMonitoringDialogOpen(false)
          setMonitoringPatient(null)
        }}
        patient={monitoringPatient}
        onSuccess={handleMonitoringSuccess}
      />

      {/* Patient Engagement Dialog */}
      <EngagementSettingsDialog
        open={engagementDialogOpen}
        onClose={() => {
          setEngagementDialogOpen(false)
          setEngagementPatient(null)
        }}
        targetType="patient"
        target={engagementPatient}
        onSuccess={handleEngagementSuccess}
      />

      {/* Rebooking Dialog */}
      <RebookingDialog
        open={rebookDialogOpen}
        onClose={() => {
          setRebookDialogOpen(false)
          setRebookVisitData(null)
        }}
        patient={selectedPatient}
        visitData={rebookVisitData}
        onSuccess={() => {
          // Refresh visit history after successful booking
          if (selectedPatient?.id) {
            loadVisitHistory(selectedPatient.id, selectedPatient.clinicId, visitHistoryEventTypes)
          }
        }}
      />

      {/* Receipt / Invoice Print Dialog */}
      <Dialog
        open={openReceiptPrintingDialog}
        PaperComponent={ReceiptPrintDialogPaper}
        onClose={() => {
          setOpenReceiptPrintingDialog(false)
          setDataForReceiptPrinting({ eventData: null, eventId: null })
        }}
        className="receipt-print-dialog"
      >
        <Suspense fallback={<Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>}>
          <ReceiptPrintingComponent
            bookedEvent={dataForReceiptPrinting.eventData}
            bookedEventId={dataForReceiptPrinting.eventId}
            patientSearchApiSignature="/api/secure/cc/support/patient/search"
            closeDialog={() => {
              setOpenReceiptPrintingDialog(false)
              setDataForReceiptPrinting({ eventData: null, eventId: null })
            }}
            fetchDataWithoutParams={getSPDataWithoutParams}
            currentClinic={receiptCurrentClinic}
            sendReceiptApiSignature="/api/secure/cc/support/receipt/send"
            postDataForSendReceipt={postSPDataWithoutParams}
          />
        </Suspense>
      </Dialog>

      {/* Joining Links Dialog */}
      <Dialog
        open={joiningLinksDialogOpen}
        onClose={() => {
          setJoiningLinksDialogOpen(false)
          setJoiningLinksEventData(null)
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LinkIcon sx={{ color: '#6366f1', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: '18px' }}>
                Joining Links
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {joiningLinksEventData?.mentorName && `with ${joiningLinksEventData.mentorName}`}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setJoiningLinksDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {joiningLinksLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">Loading joining links...</Typography>
            </Box>
          ) : joiningLinksEventData ? (
            <>
              {/* Public Link Status Summary */}
              {(() => {
                const publicLinkInfo = joiningLinksEventData?.publicJoiningLinks?.[0]
                if (!publicLinkInfo) {
                  return (
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      backgroundColor: 'rgba(245, 158, 11, 0.1)', 
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <AccessTimeIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="#92400e">
                          Public Link Not Available Yet
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Public links are generated closer to the session time
                        </Typography>
                      </Box>
                    </Box>
                  )
                }

                const validFrom = publicLinkInfo?.validFrom ? dayjs.utc(publicLinkInfo.validFrom) : null
                const expiresAt = publicLinkInfo?.expiresAt ? dayjs.utc(publicLinkInfo.expiresAt) : null
                const now = dayjs()
                const isNotValidYet = validFrom && validFrom.isAfter(now)
                const isExpired = expiresAt && expiresAt.isBefore(now)
                const isActive = !isNotValidYet && !isExpired

                if (isExpired) {
                  return (
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <CancelIcon sx={{ fontSize: 20, color: '#ef4444' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="#dc2626">
                          Public Link Expired
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Expired on {expiresAt.local().format('DD MMM YYYY, h:mm A')}
                        </Typography>
                      </Box>
                    </Box>
                  )
                }

                if (isNotValidYet) {
                  const diffMins = validFrom.diff(now, 'minute')
                  const diffHours = Math.floor(diffMins / 60)
                  const remainingMins = diffMins % 60
                  const timeUntilValid = diffHours > 0 
                    ? `${diffHours}h ${remainingMins}m` 
                    : `${diffMins} minutes`

                  return (
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      backgroundColor: 'rgba(245, 158, 11, 0.1)', 
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <AccessTimeIcon sx={{ fontSize: 20, color: '#f59e0b' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="#92400e">
                          Public Link Valid In: {timeUntilValid}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Becomes active at {validFrom.local().format('DD MMM YYYY, h:mm A')}
                        </Typography>
                      </Box>
                    </Box>
                  )
                }

                if (isActive) {
                  return (
                    <Box sx={{ 
                      mb: 2, 
                      p: 1.5, 
                      borderRadius: 2, 
                      backgroundColor: 'rgba(16, 185, 129, 0.1)', 
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <InfoIcon sx={{ fontSize: 20, color: '#10b981' }} />
                      <Box>
                        <Typography variant="body2" fontWeight={600} color="#047857">
                          Public Link is Active
                        </Typography>
                        {expiresAt && (
                          <Typography variant="caption" color="text.secondary">
                            Valid until {expiresAt.local().format('DD MMM YYYY, h:mm A')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                }

                return null
              })()}

              <JoiningDetailsContent
                bookedEventInfo={joiningLinksEventData}
                refreshBookedSlots={() => {
                  // Refresh visit history after any changes
                  if (selectedPatient?.id) {
                    loadVisitHistory(selectedPatient.id, selectedPatient.clinicId, visitHistoryEventTypes)
                  }
                }}
                showSendingLinkButtonsToSupport={true}
              />
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No joining links available
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
